import React, { useState, useEffect, useLayoutEffect, useMemo } from "react";
import {
    Calendar,
    Clock,
    FileText,
    ArrowLeft,
    AlertCircle,
    ChevronDown,
    ChevronRight,
    Building,
    MapPin,
    Loader2,
} from "lucide-react";
import { format, parse, startOfDay, subHours } from "date-fns";
import { RootState, useAppDispatch } from "../../../redux/store";
import { addTimecardRequest, getAllTimesheetRecords } from "../../../redux/features/EmployeeTimeCard/api";
import { setTopNavNameTitle } from "../../../redux/features/mobileLayout";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { setGlobalBackHandler, clearGlobalBackHandler } from "../../../hooks";
import { useNavigate } from "react-router-dom";
import { getDepartments, getSites } from "../../../redux/features/common";
import { DatePickerModal, NgwAvatar, TimePickerModal } from "../../../components/common";
import Select, { SingleValue, StylesConfig } from "react-select";
import { AllTimesheetRecord } from "../../../types/response/timecard";
import { DateTimeUtil } from "../../../utils";
import { convertDecimalHoursToHM } from "../../../utils/employeeTimeCardhelper";

type TimeFieldName = "startTime" | "endTime";
type PickerSheet = "date" | TimeFieldName | null;
type CompensationType = "OT" | "TOL";
type CompensationOption = { value: CompensationType; label: string };

const compensationOptions: CompensationOption[] = [
    { value: "OT", label: "Paid Overtime" },
    { value: "TOL", label: "Time Off in Leu" },
];

const compensationSelectStyles: StylesConfig<CompensationOption, false> = {
    control: (base, state) => ({
        ...base,
        minHeight: "44px",
        borderRadius: "1rem",
        borderColor: state.isFocused ? "#cbd5e1" : "#e2e8f0",
        backgroundColor: "#f8fafc",
        boxShadow: state.isFocused ? "0 0 0 2px #e2e8f0" : "0 1px 2px rgba(15, 23, 42, 0.06)",
        paddingLeft: "0.25rem",
        paddingRight: "0.25rem",
        cursor: "pointer",
        "&:hover": {
            borderColor: "#cbd5e1",
        },
    }),
    valueContainer: (base) => ({
        ...base,
        padding: "0 0.5rem",
    }),
    singleValue: (base) => ({
        ...base,
        color: "#0f172a",
        fontSize: "0.875rem",
        fontWeight: 500,
    }),
    input: (base) => ({
        ...base,
        color: "#0f172a",
        fontSize: "0.875rem",
    }),
    placeholder: (base) => ({
        ...base,
        color: "#94a3b8",
        fontSize: "0.875rem",
    }),
    indicatorSeparator: () => ({
        display: "none",
    }),
    dropdownIndicator: (base, state) => ({
        ...base,
        color: "#94a3b8",
        padding: "0.25rem",
        transform: state.selectProps.menuIsOpen ? "rotate(180deg)" : undefined,
        transition: "transform 150ms ease",
        "&:hover": {
            color: "#64748b",
        },
    }),
    menuPortal: (base) => ({
        ...base,
        zIndex: 9999,
    }),
    menu: (base) => ({
        ...base,
        borderRadius: "1rem",
        overflow: "hidden",
        boxShadow: "0 16px 32px rgba(15, 23, 42, 0.18)",
    }),
    option: (base, state) => ({
        ...base,
        fontSize: "0.875rem",
        backgroundColor: state.isSelected ? "#2563eb" : state.isFocused ? "#eff6ff" : "#ffffff",
        color: state.isSelected ? "#ffffff" : "#0f172a",
        cursor: "pointer",
    }),
};

const toPaddedNumber = (value: number) => value.toString().padStart(2, "0");

const parseDateValue = (value: string) => parse(value, "yyyy-MM-dd", new Date());

const formatDateValue = (value: string) => {
    if (!value) {
        return "Select date";
    }

    return format(parseDateValue(value), "EEE, MMM d");
};

const formatTimeValue = (value: string) => {
    if (!value) {
        return "Select time";
    }

    const [hoursString, minutesString] = value.split(":");
    const hours = Number(hoursString);
    const minutes = Number(minutesString);
    const meridiem = hours >= 12 ? "PM" : "AM";
    const displayHour = hours % 12 === 0 ? 12 : hours % 12;

    return `${displayHour}:${toPaddedNumber(minutes)} ${meridiem}`;
};

const parseRequestDateTime = (dateValue: string, timeValue: string) =>
    parse(`${dateValue} ${timeValue}`, "yyyy-MM-dd HH:mm", new Date());

const formatDateKey = (value: Date) => format(value, "yyyy-MM-dd");

const formatHoursWindowLabel = (value: number) => `${value} hour${value === 1 ? "" : "s"}`;
const formatCalculatedDurationLabel = (durationInMinutes: number) => {
    const hours = Math.floor(durationInMinutes / 60);
    const minutes = durationInMinutes % 60;

    if (hours > 0 && minutes > 0) {
        return `${hours} hour${hours === 1 ? "" : "s"} ${minutes} minute${minutes === 1 ? "" : "s"}`;
    }

    if (hours > 0) {
        return `${hours} hour${hours === 1 ? "" : "s"}`;
    }

    return `${minutes} minute${minutes === 1 ? "" : "s"}`;
};

const pickerFieldClassName = (hasError: boolean, hasValue: boolean, isCompact: boolean = false) =>
    `mt-1 flex ${isCompact ? "w-full min-w-0" : "min-w-[10.5rem]"} items-center justify-between gap-3 rounded-2xl border px-3 py-2 text-sm shadow-sm transition-colors ${
        hasError
            ? "border-red-400 bg-red-50 text-red-700"
            : hasValue
              ? "border-slate-200 bg-slate-50 text-slate-900"
              : "border-slate-200 bg-white text-slate-400"
    }`;

const formatRecordHours = (hours?: string) => {
    const parsedHours = Number(hours);
    return Number.isFinite(parsedHours) ? convertDecimalHoursToHM(parsedHours) : "0h 0m";
};

const getTimesheetRecordDuration = (record: AllTimesheetRecord) => {
    const calculatedDuration = DateTimeUtil.getDuration(record.start_datetime, record.end_datetime);

    if (calculatedDuration !== "0h 0m") {
        return calculatedDuration;
    }

    return formatRecordHours(record.hours);
};

const getTimesheetRecordTimeRange = (record: AllTimesheetRecord) => {
    const start = DateTimeUtil.getFormattedTime(record.start_datetime, "Missing punch");
    const end = DateTimeUtil.getFormattedTime(record.end_datetime, "Missing punch");

    return `${start} - ${end}`;
};

const getTimesheetRecordEmployeeName = (record: AllTimesheetRecord) => {
    if (record.emp_name) {
        return record.emp_name;
    }

    const employeeDetails = record.employee_details;

    return [
        employeeDetails?.employee_first_name,
        employeeDetails?.employee_middle_name,
        employeeDetails?.employee_last_name,
    ]
        .filter(Boolean)
        .join(" ")
        .trim();
};

export default function TimecardRequestForm() {
    const [date, setDate] = useState<string>("");
    const [startTime, setStartTime] = useState<string>("");
    const [endTime, setEndTime] = useState<string>("");
    const [compensationType, setCompensationType] = useState<CompensationType>("OT");
    const [requestNotes, setRequestNotes] = useState<string>("");
    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const [isAccordionOpen, setIsAccordionOpen] = useState<boolean>(false);
    const [selectedDepartment, setSelectedDepartment] = useState<string>("");
    const [selectedSite, setSelectedSite] = useState<string>("");
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [activeSheet, setActiveSheet] = useState<PickerSheet>(null);
    const [timesheetRecords, setTimesheetRecords] = useState<AllTimesheetRecord[]>([]);
    const [isLoadingTimesheetRecords, setIsLoadingTimesheetRecords] = useState<boolean>(false);
    const [timesheetRecordsError, setTimesheetRecordsError] = useState<string>("");

    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const user = useSelector((state: RootState) => state.user.user);
    const { departments, sites } = useSelector((state: RootState) => state.common);
    const { groupedTimesheets } = useSelector((state: RootState) => state.employeeTimeCardData);
    const selectedCompensationOption =
        compensationOptions.find((option) => option.value === compensationType) ?? null;
    const attendanceRequestEnableHours = Number(
        user?.ATTENDANCE_REQUEST_CONFIG?.ALLOW_PAST_HOURS_FROM_TODAY
    );
    const backdateWindowLabel = useMemo(() => {
        if (!Number.isFinite(attendanceRequestEnableHours) || attendanceRequestEnableHours <= 0) {
            return "";
        }

        return formatHoursWindowLabel(attendanceRequestEnableHours);
    }, [attendanceRequestEnableHours]);
    const minRequestDate = useMemo(() => {
        if (!Number.isFinite(attendanceRequestEnableHours) || attendanceRequestEnableHours <= 0) {
            return undefined;
        }

        return startOfDay(subHours(new Date(), attendanceRequestEnableHours));
    }, [attendanceRequestEnableHours]);
    const requestWindowStart = useMemo(() => {
        if (!Number.isFinite(attendanceRequestEnableHours) || attendanceRequestEnableHours <= 0) {
            return undefined;
        }

        return subHours(new Date(), attendanceRequestEnableHours);
    }, [attendanceRequestEnableHours]);
    const requestWindowStartDateKey = useMemo(
        () => (requestWindowStart ? formatDateKey(requestWindowStart) : ""),
        [requestWindowStart]
    );
    const minAllowedTimeForSelectedDate = useMemo(() => {
        if (!date || !requestWindowStart || date !== requestWindowStartDateKey) {
            return undefined;
        }

        return format(requestWindowStart, "HH:mm");
    }, [date, requestWindowStart, requestWindowStartDateKey]);
    const calculatedDuration = useMemo(() => {
        if (!startTime || !endTime) {
            return null;
        }

        const baseDate = date || format(new Date(), "yyyy-MM-dd");
        const startDateTime = parseRequestDateTime(baseDate, startTime);
        const endDateTime = parseRequestDateTime(baseDate, endTime);
        const durationInMinutes = Math.floor(
            (endDateTime.getTime() - startDateTime.getTime()) / (1000 * 60)
        );

        if (durationInMinutes <= 0) {
            return null;
        }

        return {
            durationLabel: formatCalculatedDurationLabel(durationInMinutes),
        };
    }, [date, startTime, endTime]);
    const datePickerDescription = minRequestDate
        ? `Select a work date from the previous ${backdateWindowLabel} onward.`
        : "Today and future dates are available.";
    const employeeDisplayName = useMemo(
        () =>
            [user?.os_emp_first_name, user?.os_emp_last_name].filter(Boolean).join(" ").trim() ||
            groupedTimesheets?.data?.emp_name ||
            "Employee",
        [groupedTimesheets?.data?.emp_name, user?.os_emp_first_name, user?.os_emp_last_name]
    );
    const employeeImageUrl = useMemo(
        () => groupedTimesheets?.data?.image_url || user?.os_emp_image_name || null,
        [groupedTimesheets?.data?.image_url, user?.os_emp_image_name]
    );
    const employeeId = useMemo(() => String(user?.os_emp_account_id || ""), [user?.os_emp_account_id]);
    const departmentDisplay = useMemo(() => {
        const departmentCode = user?.department_code || "";
        const departmentName = departments.find(
            (department) =>
                String(department.department_code || department.code || "") === String(departmentCode)
        )?.department_name;

        return departmentName || departmentCode;
    }, [departments, user?.department_code]);

    useEffect(() => {
        if (departments.length === 0) {
            dispatch(getDepartments());
        }
        if (sites.length === 0) {
            dispatch(getSites());
        }
    }, [dispatch, departments.length, sites.length]);

    useEffect(() => {
        if (!date) {
            setTimesheetRecords([]);
            setTimesheetRecordsError("");
            return;
        }

        const selectedDate = parseDateValue(date);

        if (minRequestDate && selectedDate < minRequestDate) {
            setDate(format(minRequestDate, "yyyy-MM-dd"));
        }
    }, [date, minRequestDate]);

    useEffect(() => {
        if (!date) {
            return;
        }

        if (!employeeId) {
            setTimesheetRecords([]);
            setTimesheetRecordsError("Employee ID is unavailable.");
            return;
        }

        let ignoreResponse = false;

        const fetchTimesheetRecords = async () => {
            setIsLoadingTimesheetRecords(true);
            setTimesheetRecordsError("");

            try {
                const response = await dispatch(
                    getAllTimesheetRecords({
                        start_date: date,
                        end_date: date,
                        employee_id: employeeId,
                    })
                ).unwrap();

                if (ignoreResponse) {
                    return;
                }

                if (response.status) {
                    setTimesheetRecords(response.data || []);
                } else {
                    setTimesheetRecords([]);
                    setTimesheetRecordsError(response.message || "Failed to load timesheet records.");
                }
            } catch (error: any) {
                if (ignoreResponse) {
                    return;
                }

                setTimesheetRecords([]);
                setTimesheetRecordsError(
                    typeof error === "string"
                        ? error
                        : error?.message || error?.message?.toString() || "Failed to load timesheet records."
                );
            } finally {
                if (!ignoreResponse) {
                    setIsLoadingTimesheetRecords(false);
                }
            }
        };

        fetchTimesheetRecords();

        return () => {
            ignoreResponse = true;
        };
    }, [date, dispatch, employeeId]);

    const getValidationErrors = () => {
        const newErrors: { [key: string]: string } = {};

        if (!date) {
            newErrors.date = "Date is required";
        }

        if (!startTime) {
            newErrors.startTime = "Start time is required";
        }

        if (!endTime) {
            newErrors.endTime = "End time is required";
        }

        if (date && startTime && endTime) {
            const startDateTime = parseRequestDateTime(date, startTime);
            const endDateTime = parseRequestDateTime(date, endTime);

            if (endDateTime <= startDateTime) {
                newErrors.endTime = "End time must be after start time";
            }

            if (requestWindowStart && startDateTime < requestWindowStart) {
                newErrors.startTime = `Start time must be within the previous ${backdateWindowLabel}`;
            }

            if (requestWindowStart && endDateTime < requestWindowStart) {
                newErrors.endTime = `End time must be within the previous ${backdateWindowLabel}`;
            }
        }

        return newErrors;
    };

    const Line = () => <hr className="border-gray-200" />;

    const openDatePicker = () => {
        setActiveSheet("date");
    };

    const openTimePicker = (field: TimeFieldName) => {
        setActiveSheet(field);
    };

    const closeSheet = () => {
        setActiveSheet(null);
    };

    const handleDateChange = (value: string) => {
        setDate(value);
        setErrors((prev) => ({ ...prev, date: "" }));
    };

    const handleTimeApply = (field: TimeFieldName, nextValue: string) => {
        if (field === "startTime") {
            setStartTime(nextValue);
            setErrors((prev) => ({ ...prev, startTime: "" }));
        } else {
            setEndTime(nextValue);
            setErrors((prev) => ({ ...prev, endTime: "" }));
        }

        closeSheet();
    };

    const handleCompensationTypeChange = (selectedOption: SingleValue<CompensationOption>) => {
        if (!selectedOption) {
            return;
        }

        setCompensationType(selectedOption.value);
    };

    const handleSubmit = async () => {
        const newErrors = getValidationErrors();

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            Object.values(newErrors).forEach((message) => toast.error(message));
            return;
        }

        setIsSubmitting(true);
        try {
            const startDatetime = `${date} ${startTime}:00`;
            const endDatetime = endTime ? `${date} ${endTime}:00` : "";

            const payload = {
                record_type: "A" as const,
                earning_code: compensationType,
                start_datetime: startDatetime,
                end_datetime: endDatetime,
                breaktimes_json: [],
                request_notes: requestNotes,
                attributes: {
                    ...(selectedDepartment && { DEPARTMENT: selectedDepartment }),
                    ...(selectedSite && { site: selectedSite })
                },
                other_details_json: {}
            };

            const response = await dispatch(addTimecardRequest(payload)).unwrap();
            
            if (response.status) {
                toast.success(response.message || "Timecard request submitted successfully!");
                navigate(-1); // Go back to previous page
            } else {
                toast.error("Failed to submit timecard request");
            }
        } catch (error: any) {
            toast.error(error || "An error occurred while submitting the request");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleBackClick = () => {
        navigate(-1);
    };

    useLayoutEffect(() => {
        dispatch(setTopNavNameTitle("Timecard Request"));

        setGlobalBackHandler(() => {
            navigate(-1);
            return true;
        });

        return () => {
            clearGlobalBackHandler();
        };
    }, [dispatch, navigate]);

    return (
        <div className="p-4 space-y-0 bg-white max-w-md mx-auto">
            {/* Back Button and Page Title for Web */}
            <div className="flex items-center justify-between mb-6 md:flex hidden">
                <button
                    onClick={handleBackClick}
                    className="flex items-center gap-2 text-gray-600 hover:text-gray-800 transition-colors"
                >
                    <ArrowLeft size={20} />
                    <span>Back</span>
                </button>
                <h1 className="text-2xl font-bold text-gray-800">Timecard Request</h1>
            </div>

            <div className="mb-4 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
                <div className="flex items-start gap-3">
                    <NgwAvatar src={employeeImageUrl} name={employeeDisplayName} className="w-12 h-12" />
                    <div className="min-w-0 flex-1">
                        <div className="mt-1 truncate text-lg font-semibold text-gray-900">
                            {employeeDisplayName}
                        </div>
                        <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-sm text-gray-600">
                            {departmentDisplay && <span>{departmentDisplay}</span>}
                        </div>
                    </div>
                </div>
            </div>

            {/* Compensation Type */}
            <div className="flex justify-between items-center py-3 text-gray-800">
                <span className="font-medium">Compensation Type</span>
                <div className="flex w-[11rem] min-w-[11rem] flex-col items-end sm:w-[12.5rem] sm:min-w-[12.5rem]">
                    <Select
                        value={selectedCompensationOption}
                        onChange={handleCompensationTypeChange}
                        options={compensationOptions}
                        isSearchable={false}
                        className="mt-1 w-full"
                        classNamePrefix="react-select"
                        styles={compensationSelectStyles}
                        menuPortalTarget={typeof document === "undefined" ? undefined : document.body}
                    />
                </div>
            </div>
            <Line />

            {/* Date */}
            <div className="flex justify-between items-start py-3">
                <div className="flex items-center gap-2">
                    <Calendar className="text-gray-600" />
                    <span className="text-[15px] font-medium text-gray-800">Date *</span>
                </div>
                <div className="flex flex-col items-end">
                    <button
                        type="button"
                        onClick={openDatePicker}
                        className={pickerFieldClassName(Boolean(errors.date), Boolean(date))}
                    >
                        <span className="font-medium">{formatDateValue(date)}</span>
                        <ChevronDown size={16} className="shrink-0 text-slate-400" />
                    </button>
                    {errors.date && (
                        <div className="flex items-center gap-1 mt-1 text-red-500 text-xs">
                            <AlertCircle size={12} />
                            <span>{errors.date}</span>
                        </div>
                    )}
                </div>
            </div>
            <Line />

            {date && (
                <>
                    <div className="py-3">
                        <div className="mb-3 flex items-center justify-between gap-3">
                            <div>
                                <p className="text-[15px] font-semibold text-gray-800">Timesheet Records</p>
                                <p className="text-xs text-gray-500">{formatDateValue(date)}</p>
                            </div>
                            {isLoadingTimesheetRecords ? (
                                <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                            ) : (
                                <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600">
                                    {timesheetRecords.length}
                                </span>
                            )}
                        </div>

                        {timesheetRecordsError ? (
                            <div className="rounded-xl border border-red-100 bg-red-50 px-3 py-2.5 text-sm text-red-700">
                                {timesheetRecordsError}
                            </div>
                        ) : isLoadingTimesheetRecords ? (
                            <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-3 text-sm text-slate-500">
                                <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
                                <span>Loading records...</span>
                            </div>
                        ) : timesheetRecords.length > 0 ? (
                            <ul className="space-y-2">
                                {timesheetRecords.map((record) => {
                                    const employeeName = getTimesheetRecordEmployeeName(record);
                                    const earningCode = record.earning_code?.name || record.earning_code?.code || "Uncoded";
                                    const department = record.department?.name || record.department?.code;
                                    const site = record.site?.name || record.site?.code;

                                    return (
                                        <li
                                            key={record.id}
                                            className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-3"
                                        >
                                            <div className="flex items-start justify-between gap-3">
                                                <div className="min-w-0 flex-1">
                                                    <p className="truncate text-sm font-semibold text-slate-900">
                                                        {getTimesheetRecordTimeRange(record)}
                                                    </p>
                                                    <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-slate-500">
                                                        <span className="font-medium text-slate-700">
                                                            {getTimesheetRecordDuration(record)}
                                                        </span>
                                                        <span aria-hidden>-</span>
                                                        <span>{earningCode}</span>
                                                        {employeeName && (
                                                            <>
                                                                <span aria-hidden>-</span>
                                                                <span>{employeeName}</span>
                                                            </>
                                                        )}
                                                    </div>
                                                    {(department || site) && (
                                                        <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-slate-500">
                                                            {department && <span>{department}</span>}
                                                            {department && site && <span aria-hidden>-</span>}
                                                            {site && <span>{site}</span>}
                                                        </div>
                                                    )}
                                                </div>
                                                <span className="shrink-0 rounded-full bg-white px-2 py-0.5 text-xs font-medium text-slate-600">
                                                    {formatRecordHours(record.hours)}
                                                </span>
                                            </div>
                                        </li>
                                    );
                                })}
                            </ul>
                        ) : (
                            <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-3 text-sm text-slate-500">
                                No timesheet records found for this date.
                            </div>
                        )}
                    </div>
                    <Line />
                </>
            )}

            {/* Time Range */}
            <div className="py-3">
                <div className="grid grid-cols-2 gap-3">
                    <div className="min-w-0">
                        <div className="flex items-center gap-2">
                            <Clock className="text-gray-600" />
                            <span className="text-[15px] font-medium text-gray-800">Start Time *</span>
                        </div>
                        <button
                            type="button"
                            onClick={() => openTimePicker("startTime")}
                            className={pickerFieldClassName(Boolean(errors.startTime), Boolean(startTime), true)}
                        >
                            <span className="min-w-0 truncate font-medium">{formatTimeValue(startTime)}</span>
                            <ChevronDown size={16} className="shrink-0 text-slate-400" />
                        </button>
                        {errors.startTime && (
                            <div className="flex items-center gap-1 mt-1 text-red-500 text-xs">
                                <AlertCircle size={12} />
                                <span>{errors.startTime}</span>
                            </div>
                        )}
                    </div>

                    <div className="min-w-0">
                        <div className="flex items-center gap-2">
                            <Clock className="text-gray-600" />
                            <span className="text-[15px] font-medium text-gray-800">End Time *</span>
                        </div>
                        <button
                            type="button"
                            onClick={() => openTimePicker("endTime")}
                            className={pickerFieldClassName(Boolean(errors.endTime), Boolean(endTime), true)}
                        >
                            <span className="min-w-0 truncate font-medium">{formatTimeValue(endTime)}</span>
                            <ChevronDown size={16} className="shrink-0 text-slate-400" />
                        </button>
                        {errors.endTime && (
                            <div className="flex items-center gap-1 mt-1 text-red-500 text-xs">
                                <AlertCircle size={12} />
                                <span>{errors.endTime}</span>
                            </div>
                        )}
                    </div>
                </div>
                {calculatedDuration ? (
                    <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5">
                        <p className="text-xs text-slate-500">
                            <span className="font-medium text-slate-600">Duration:</span>
                            <span className="ml-1 font-semibold text-blue-600">
                                {calculatedDuration.durationLabel}
                            </span>
                        </p>
                    </div>
                ) : null}
            </div>
            <Line />

            {/* Request Notes */}
            <div className="py-3">
                <div className="flex items-center gap-2 mb-1">
                    <FileText className="text-gray-600" />
                    <span className="text-[15px] font-medium text-gray-800">Request Notes</span>
                </div>
                <textarea
                    value={requestNotes}
                    onChange={(e) => setRequestNotes(e.target.value)}
                    className="w-full border border-gray-300 rounded px-2 py-1 mt-2"
                    rows={3}
                    placeholder="Enter request notes..."
                />
            </div>
            <Line />

            {/* Additional Information Accordion */}
            <div className="py-3">
                <button
                    onClick={() => setIsAccordionOpen(!isAccordionOpen)}
                    className="flex items-center justify-between w-full text-left"
                >
                    <span className="font-medium text-gray-800">Additional Details</span>
                    {isAccordionOpen ? (
                        <ChevronDown className="text-gray-600" size={20} />
                    ) : (
                        <ChevronRight className="text-gray-600" size={20} />
                    )}
                </button>

                    {isAccordionOpen && (
                    <div className="mt-3 space-y-3">
                        {/* Department Dropdown */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                <Building className="inline w-4 h-4 mr-1" />
                                Department
                            </label>
                            <select
                                value={selectedDepartment}
                                onChange={(e) => setSelectedDepartment(e.target.value)}
                                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:border-gray-500 focus:ring focus:ring-gray-200"
                            >
                                <option value="">Select a department</option>
                                {departments.map((dept) => (
                                    <option
                                        key={dept.os_department_id || dept.code}
                                        value={dept.department_code || dept.code || ""}
                                    >
                                        {dept.department_name || dept.name || "Unknown Department"}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Site Dropdown */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                <MapPin className="inline w-4 h-4 mr-1" />
                                Site
                            </label>
                            <select
                                value={selectedSite}
                                onChange={(e) => setSelectedSite(e.target.value)}
                                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:border-gray-500 focus:ring focus:ring-gray-200"
                            >
                                <option value="">Select a site</option>
                                {sites.map((site) => (
                                    <option
                                        key={site.us_user_site_profile_id || site.us_site_code}
                                        value={site.us_site_code || ""}
                                    >
                                        {site.user_site_name || "Unknown Site"}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                )}
            </div>

            {/* Submit button */}
            <div className="pt-4">
                <button
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    className={`w-full rounded border px-4 py-2 font-medium transition-colors ${
                        isSubmitting
                            ? "border-gray-300 text-gray-400 bg-gray-100 cursor-not-allowed"
                            : "border-blue-600 text-blue-600 bg-transparent hover:bg-blue-50"
                    }`}
                >
                    {isSubmitting ? "Submitting..." : "Submit Request"}
                </button>
            </div>

            <DatePickerModal
                open={activeSheet === "date"}
                value={date}
                onChange={handleDateChange}
                onClose={closeSheet}
                title="Choose a work date"
                description={datePickerDescription}
                minDate={minRequestDate}
            />

            <TimePickerModal
                open={activeSheet === "startTime"}
                value={startTime}
                label="Start Time"
                onApply={(value) => handleTimeApply("startTime", value)}
                onClose={closeSheet}
                title="Choose a start time"
                description="Set the exact time for this request."
                minTime={minAllowedTimeForSelectedDate}
            />

            <TimePickerModal
                open={activeSheet === "endTime"}
                value={endTime}
                label="End Time"
                onApply={(value) => handleTimeApply("endTime", value)}
                onClose={closeSheet}
                title="Choose an end time"
                description="Set the exact time for this request."
                minTime={minAllowedTimeForSelectedDate}
            />
        </div>
    );
}
